// request-logger.ts
// Central utility to log API errors/issues into IndexedDB (kept for 24h) without extra packages.

import axios, { AxiosRequestConfig, AxiosInstance } from "axios";

let _dbPromise: Promise<IDBDatabase> | null = null;

/** Sanitize objects for IndexedDB by stripping out non-cloneable values */
function sanitizeForIndexedDB(obj: any): any {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    // Fallback: convert all values to strings
    if (obj && typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, String(value)])
      );
    }
    return obj;
  }
}

/** Check for IndexedDB support */
function isIndexedDBAvailable(): boolean {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}

// Log entry schema
export interface RequestLog {
  id?: number;
  url: string;
  method: string;
  headers: Record<string, string>;
  payload: any;
  timestamp: number; // keeping as string to avoid schema bump right now
  status?: number;
  responseTimeMs?: number;
  error?: string;
  response?: any;
}

const DB_NAME = "ErrorRequestLogsDB";
const STORE_NAME = "logs";
const DB_VERSION = 1;



/** Open (or reuse) an IndexedDB connection */
function openDB(): Promise<IDBDatabase> {
  if (!isIndexedDBAvailable()) {
    return Promise.reject(new Error("IndexedDB is not available in this environment"));
  }
  // Reuse the same connection/promise if already opening/open
  if (_dbPromise) return _dbPromise;

  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
        store.createIndex("timestamp", "timestamp");
      }
    };

    req.onsuccess = () => {
      const db = req.result;
      // If a schema upgrade happens elsewhere, close and allow a new connection to be made next time
      // CHECK THIS :: If upgrading the DB_VERSION constant in future. Check this method.
      db.onversionchange = () => {
        try { db.close(); } catch {}
        _dbPromise = null;
      };
      resolve(db);
      // opportunistic cleanup (non-blocking)
      setTimeout(() => { maybeCleanup(); }, 0);
    };

    req.onerror = () => {
      // Clear the cached promise so we can try again next call
      _dbPromise = null;
      reject(req.error);
    };

    req.onblocked = () => {
      // Another tab may be holding an older version open
      console.warn("IndexedDB open blocked—another tab may be using an old database version.");
    };
  });

  return _dbPromise;
}

/** Helper to perform a transaction */
async function withStore<T>(mode: IDBTransactionMode, callback: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const request = callback(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Add a new log entry */
export function addLogEntry(entry: Omit<RequestLog, "id">): Promise<IDBValidKey> {
  return withStore("readwrite", (store) => {
    const req = store.add(entry);
    // opportunistic cleanup after writes
    setTimeout(() => { maybeCleanup(); }, 0);
    return req;
  });
}

/** Update an existing log entry by ID */
export async function updateLogEntry(id: number, updates: Partial<RequestLog>): Promise<void> {
  const db = await openDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result as RequestLog;
      // merge updates into existing record, ensuring id is preserved
      const updated: RequestLog = { ...record, ...updates, id };
      const putReq = store.put(updated);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/** Remove entries older than 24h */
export async function cleanOldLogs(): Promise<void> {
  if (!isIndexedDBAvailable()) return;
  const cutoff = Date.now() - 23 * 60 * 1000;
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("timestamp");
  const range = IDBKeyRange.upperBound(cutoff, true);
  index.openCursor(range).onsuccess = (event) => {
    const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
    if (cursor) {
      store.delete(cursor.primaryKey as number);
      cursor.continue();
    }
  };
}

function deleteDB(dbName: string = DB_NAME): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(dbName);

    req.onsuccess = () => {
      console.log(`Database "${dbName}" deleted successfully.`);
      resolve();
    };

    req.onerror = () => {
      console.error(`Error deleting database "${dbName}":`, req.error);
      reject(req.error);
    };

    req.onblocked = () => {
      console.warn(`Delete blocked. Please close other tabs or connections using "${dbName}".`);
    };
  });
}


async function dropLogsDB(): Promise<void> {
  await deleteDB("RequestLogsDB");
}



const CLEANUP_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const LAST_CLEANUP_KEY = 'ErrorRequestLogsDB:lastCleanupTs';

function shouldRunCleanup(): boolean {
  try {
    const last = Number(localStorage.getItem(LAST_CLEANUP_KEY) || '0');
    const now = Date.now();
    if (now - last > CLEANUP_INTERVAL_MS) {
      localStorage.setItem(LAST_CLEANUP_KEY, String(now));
      return true;
    }
  } catch {}
  return false;
}

async function maybeCleanup(): Promise<void> {
  if (!isIndexedDBAvailable()) return;
  if (!shouldRunCleanup()) return;
  try {
    const navAny: any = navigator as any;
    if (navAny?.locks?.request) {
      await navAny.locks.request('request-logs-cleanup', { mode: 'exclusive', ifAvailable: true }, async (lock: any) => {
		if(!lock) return;
        await cleanOldLogs();
      });
    } else {
      await cleanOldLogs();
    }
  } catch (e) {
    console.warn('RequestLogs cleanup failed:', e);
  }
}

/**
 * Creates an Axios instance that logs request/response details into IndexedDB.
 */
export function createLoggedAxiosInstance(config?: AxiosRequestConfig): AxiosInstance {
  // clarifyTimeoutError makes real timeouts surface as ETIMEDOUT, distinct from
  // ECONNABORTED which axios uses for any cancellation (including background-tab
  // freeze-induced aborts). Lets ApiUtils.axiosError ignore aborts without losing
  // visibility into genuine server-side timeouts.
  const instance = axios.create({
    ...config,
    transitional: {
      ...(config?.transitional ?? {}),
      clarifyTimeoutError: true,
    },
  });

  // Request interceptor: record only timing metadata; do NOT persist here
  instance.interceptors.request.use(async (req) => {
    const start = Date.now();
    (req as any).metadata = { start };
    return req;
  });

  // Response interceptor: persist ONLY failures (4xx/5xx), timeouts, or generic network errors
  instance.interceptors.response.use(
    async (res) => {
      // Do nothing for 2xx/3xx
      return res;
    },
    async (error) => {
      const cfg = error.config || {};
      const meta = (cfg as any).metadata || {};
      const duration = meta.start ? (Date.now() - meta.start) : undefined;

      const status: number | undefined = error?.response?.status;
      const isTimeout = error?.code === 'ECONNABORTED';
      const isCanceled = error?.code === 'ERR_CANCELED';

      try {
        // Log if it's a 4xx/5xx, a timeout, or a generic network error (no response). Skip canceled requests.
        if ((typeof status === 'number' && status >= 400) || isTimeout || (!status && !isCanceled)) {
			console.log("error response: ", error.response);
			
          await addLogEntry({
            url: cfg.url || '',
            method: (cfg.method || 'get').toUpperCase(),
            headers: sanitizeForIndexedDB(cfg.headers),
            payload: sanitizeForIndexedDB(cfg.data),
            timestamp: new Date(meta.start || Date.now()).getTime(),
            status: typeof status === 'number' ? status : 0,
            responseTimeMs: duration,
            error: isTimeout ? 'Request timeout' : (error?.message || 'Network error'),
			response: sanitizeForIndexedDB(error?.response?.data)
          });
        }
      } catch (e) {
        console.warn('Error logging failure skipped:', e);
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

dropLogsDB()